import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes } from 'react-router-dom';
import { initAnalytics } from './lib/analytics';
import { preloadRoutePages } from './lib/lazyRoute';
import { trackPageLoadTiming } from './lib/pageTiming';
import { captureMutationError, type ExpectedRejection } from './sentry.config';
import { AppErrorFallback } from './components/AppErrorFallback';
import { PageLoader } from './components/ui/PageLoader';
import { Toaster } from './components/ui/Sonner';
import { appRoutes } from './routes';

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      captureMutationError(
        error,
        mutation.options.mutationKey,
        mutation.options.meta?.expectsRejection as ExpectedRejection | undefined
      ),
  }),
});

function App() {
  useEffect(() => {
    // Analytics + session replay (PostHog). Initialized synchronously rather
    // than on idle: the onboarding funnels fire their first step event from a
    // mount effect, and anything captured before init is dropped. PostHog
    // fetches the replay recorder itself, after init, off the critical path.
    initAnalytics();
    // After init: a capture queued before it is dropped.
    trackPageLoadTiming();
  }, []);

  // Beside the auth check, which AuthProvider starts in this same commit.
  useEffect(() => {
    preloadRoutePages(appRoutes(), window.location.pathname);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>{appRoutes()}</Routes>
          </Suspense>
        </Router>
        <Toaster />
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
